import { z } from 'zod';

const identifierSchema = z.string().trim().min(1).max(160);
const tenantIdSchema = z.string().uuid();
const timestampSchema = z.string().datetime({ offset: true });

export const inAppNotificationSchema = z.object({
  id: identifierSchema,
  scope: z.enum(['tenant', 'platform']),
  tenantId: tenantIdSchema.optional(),
  title: z.string().trim().min(1).max(120),
  body: z.string().trim().min(1).max(2_000),
  createdAt: timestampSchema,
  createdBy: identifierSchema,
  status: z.enum(['draft', 'approved', 'scheduled', 'published', 'withdrawn']).default('published'),
  scheduledFor: timestampSchema.optional(),
  expiresAt: timestampSchema.optional(),
  retentionExpiresAt: timestampSchema.optional(),
  version: z.number().int().positive().default(1),
  acknowledgementRequired: z.boolean().default(false),
  securityMandatory: z.boolean().default(false),
  mandatoryCategory: z.enum(['security', 'privacy', 'billing', 'terms']).optional(),
  isTest: z.boolean().default(false),
});
export type InAppNotification = z.infer<typeof inAppNotificationSchema>;

export const inAppNotificationListItemSchema = inAppNotificationSchema.extend({
  readAt: timestampSchema.optional(),
  acknowledgedAt: timestampSchema.optional(),
});
export type InAppNotificationListItem = z.infer<typeof inAppNotificationListItemSchema>;

export const createInAppNotificationSchema = inAppNotificationSchema.pick({
  title: true,
  body: true,
  status: true,
  scheduledFor: true,
  expiresAt: true,
  acknowledgementRequired: true,
  securityMandatory: true,
  mandatoryCategory: true,
  isTest: true,
});
export type CreateInAppNotification = z.infer<typeof createInAppNotificationSchema>;

export const notificationReadMarkerSchema = z.object({
  tenantId: tenantIdSchema,
  notificationId: identifierSchema,
  userId: identifierSchema,
  readAt: timestampSchema,
});
export type NotificationReadMarker = z.infer<typeof notificationReadMarkerSchema>;

export const notificationAcknowledgementSchema = notificationReadMarkerSchema.extend({
  acknowledgedAt: timestampSchema,
});
export type NotificationAcknowledgement = z.infer<typeof notificationAcknowledgementSchema>;

export const communicationPreferencesSchema = z.object({
  tenantId: tenantIdSchema,
  userId: identifierSchema,
  inAppEnabled: z.boolean().default(true),
  updatedAt: timestampSchema,
  updatedBy: identifierSchema,
});
export type CommunicationPreferences = z.infer<typeof communicationPreferencesSchema>;
export const updateCommunicationPreferencesSchema = communicationPreferencesSchema.pick({
  inAppEnabled: true,
});

export const tenantCommunicationPolicySchema = z.object({
  tenantId: tenantIdSchema,
  readReceiptsEnabled: z.boolean().default(false),
  messageRetentionDays: z.number().int().min(30).max(2_555).default(400),
  notificationRetentionDays: z.number().int().min(30).max(2_555).default(400),
  calendarRetentionDays: z.number().int().min(30).max(2_555).default(400),
  updatedAt: timestampSchema,
  updatedBy: identifierSchema,
});
export type TenantCommunicationPolicy = z.infer<typeof tenantCommunicationPolicySchema>;
export const updateTenantCommunicationPolicySchema = tenantCommunicationPolicySchema.pick({
  readReceiptsEnabled: true,
  messageRetentionDays: true,
  notificationRetentionDays: true,
  calendarRetentionDays: true,
});

export const calendarEventSchema = z.object({
  id: identifierSchema,
  tenantId: tenantIdSchema,
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().min(1).max(5_000).optional(),
  startsAt: timestampSchema,
  endsAt: timestampSchema,
  allDay: z.boolean(),
  timeZone: z.string().trim().min(1).max(80).default('UTC'),
  visibility: z.enum(['private', 'tenant']).default('private'),
  attendeeUserIds: z.array(identifierSchema).max(100).default([]),
  reminderMinutesBefore: z.array(z.number().int().min(0).max(40_320)).max(10).default([]),
  recurrence: z
    .object({
      frequency: z.enum(['daily', 'weekly', 'monthly']),
      interval: z.number().int().min(1).max(365).default(1),
      until: timestampSchema.optional(),
      count: z.number().int().min(1).max(500).optional(),
    })
    .optional(),
  retentionExpiresAt: timestampSchema.optional(),
  createdAt: timestampSchema,
  createdBy: identifierSchema,
  updatedAt: timestampSchema,
  updatedBy: identifierSchema,
});
export type CalendarEvent = z.infer<typeof calendarEventSchema>;

const calendarEventMutationSchema = z.object({
  title: calendarEventSchema.shape.title,
  description: calendarEventSchema.shape.description.unwrap().nullable().optional(),
  startsAt: calendarEventSchema.shape.startsAt,
  endsAt: calendarEventSchema.shape.endsAt,
  allDay: calendarEventSchema.shape.allDay,
  timeZone: calendarEventSchema.shape.timeZone,
  visibility: calendarEventSchema.shape.visibility,
  attendeeUserIds: calendarEventSchema.shape.attendeeUserIds,
  reminderMinutesBefore: calendarEventSchema.shape.reminderMinutesBefore,
  recurrence: calendarEventSchema.shape.recurrence,
});

function validateCalendarWindow(
  value: { startsAt?: string | undefined; endsAt?: string | undefined },
  context: z.RefinementCtx,
): void {
  if (
    value.startsAt &&
    value.endsAt &&
    new Date(value.endsAt).getTime() <= new Date(value.startsAt).getTime()
  ) {
    context.addIssue({
      code: 'custom',
      path: ['endsAt'],
      message: 'Calendar event end must be later than its start',
    });
  }
}

export const createCalendarEventSchema =
  calendarEventMutationSchema.superRefine(validateCalendarWindow);
export type CreateCalendarEvent = z.infer<typeof createCalendarEventSchema>;

export const updateCalendarEventSchema = calendarEventMutationSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'At least one field must be updated')
  .superRefine(validateCalendarWindow);
export type UpdateCalendarEvent = z.infer<typeof updateCalendarEventSchema>;

export const tenantConversationSchema = z.object({
  id: identifierSchema,
  tenantId: tenantIdSchema,
  title: z.string().trim().min(1).max(160).optional(),
  participantUserIds: z
    .array(identifierSchema)
    .min(2)
    .max(50)
    .refine((items) => new Set(items).size === items.length, 'Participants must be unique'),
  createdAt: timestampSchema,
  createdBy: identifierSchema,
  updatedAt: timestampSchema,
});
export type TenantConversation = z.infer<typeof tenantConversationSchema>;

export const tenantMessageSchema = z.object({
  id: identifierSchema,
  tenantId: tenantIdSchema,
  conversationId: identifierSchema,
  body: z.string().trim().min(1).max(10_000),
  attachmentFileIds: z
    .array(identifierSchema)
    .max(10)
    .refine((items) => new Set(items).size === items.length, 'Attachments must be unique')
    .default([]),
  senderUserId: identifierSchema,
  createdAt: timestampSchema,
  retentionExpiresAt: timestampSchema.optional(),
  moderationStatus: z.enum(['visible', 'hidden']).default('visible'),
  deliveryStatus: z.enum(['accepted', 'delivered']).default('delivered'),
  deliveredAt: timestampSchema.optional(),
});
export type TenantMessage = z.infer<typeof tenantMessageSchema>;

export const createTenantConversationSchema = z.object({
  title: tenantConversationSchema.shape.title,
  participantUserIds: z
    .array(identifierSchema)
    .min(1)
    .max(49)
    .refine((items) => new Set(items).size === items.length, 'Participants must be unique'),
  initialMessage: tenantMessageSchema.shape.body.optional(),
});
export type CreateTenantConversation = z.infer<typeof createTenantConversationSchema>;

export const sendTenantMessageSchema = tenantMessageSchema.pick({
  body: true,
  attachmentFileIds: true,
});
export type SendTenantMessage = z.infer<typeof sendTenantMessageSchema>;

export const messageParticipantSchema = z.object({
  userId: identifierSchema,
  email: z.email(),
});
export type MessageParticipant = z.infer<typeof messageParticipantSchema>;

export const platformBroadcastMessageSchema = z.object({
  id: identifierSchema,
  scope: z.literal('platform'),
  readOnly: z.literal(true),
  subject: z.string().trim().min(1).max(160),
  body: z.string().trim().min(1).max(10_000),
  createdAt: timestampSchema,
  createdBy: identifierSchema,
  status: z.enum(['draft', 'approved', 'scheduled', 'published', 'withdrawn']).default('published'),
  scheduledFor: timestampSchema.optional(),
  expiresAt: timestampSchema.optional(),
  retentionExpiresAt: timestampSchema.optional(),
  version: z.number().int().positive().default(1),
  acknowledgementRequired: z.boolean().default(false),
  isTest: z.boolean().default(false),
});
export type PlatformBroadcastMessage = z.infer<typeof platformBroadcastMessageSchema>;

export const createPlatformBroadcastMessageSchema = platformBroadcastMessageSchema.pick({
  subject: true,
  body: true,
  status: true,
  scheduledFor: true,
  expiresAt: true,
  acknowledgementRequired: true,
  isTest: true,
});
export type CreatePlatformBroadcastMessage = z.infer<typeof createPlatformBroadcastMessageSchema>;

export const messageReadMarkerSchema = z.object({
  tenantId: tenantIdSchema,
  conversationId: identifierSchema,
  messageId: identifierSchema,
  userId: identifierSchema,
  readAt: timestampSchema,
});
export type MessageReadMarker = z.infer<typeof messageReadMarkerSchema>;

export const messageReportSchema = z.object({
  id: identifierSchema,
  tenantId: tenantIdSchema,
  conversationId: identifierSchema,
  messageId: identifierSchema,
  reportedBy: identifierSchema,
  reason: z.enum(['spam', 'harassment', 'privacy', 'other']),
  details: z.string().trim().min(1).max(1_000).optional(),
  status: z.enum(['open', 'resolved', 'dismissed']),
  createdAt: timestampSchema,
  resolvedAt: timestampSchema.optional(),
  resolvedBy: identifierSchema.optional(),
  resolutionNote: z.string().trim().min(1).max(500).optional(),
});
export type MessageReport = z.infer<typeof messageReportSchema>;
export const createMessageReportSchema = messageReportSchema.pick({ reason: true, details: true });
export const resolveMessageReportSchema = z.object({
  status: z.enum(['resolved', 'dismissed']),
  resolutionNote: messageReportSchema.shape.resolutionNote,
});
export const moderateMessageSchema = z.object({
  moderationStatus: z.enum(['visible', 'hidden']),
});

export const importCalendarIcsSchema = z.object({
  ics: z.string().min(1).max(200_000),
  visibility: z.enum(['private', 'tenant']).default('private'),
});
